import React, { useCallback, useState } from 'react';
// Actions
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { getUI, getUser } from 'redux/selectors';
import * as UserActions from 'redux/actions/user';
import { updateUI } from 'redux/actions/ui';

// Components
import Dialog from '@material-ui/core/Dialog';
import { MERGE } from 'common/constant';

const MergeAccountsDialog = ({
    close,
    ui,
    user,
    dispatch,
    open,
    onSuccess
}) => {
    const [step, setStep] = useState('details');
    const [secondaryEmail, setSecondaryEmail] = useState('');
    const [confirmCode, setConfirmCode] = useState('');
    const [emailTouched, setEmailTouched] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [serverError, setServerError] = useState('');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid = emailRegex.test(secondaryEmail.trim());
    const isConfirmValid = confirmCode.trim() === MERGE;
    const canContinue = isEmailValid && isConfirmValid && !isSubmitting;

    const extractSecondaryEmailValidation = useCallback((responseOrError) => {
        const validationErrors =
            responseOrError?.error?.response?.data?.validation_errors ||
            responseOrError?.response?.data?.validation_errors ||
            responseOrError?.payload?.data?.validation_errors;

        const emailErrors = validationErrors?.secondary_account_email;
        if (Array.isArray(emailErrors) && emailErrors.length > 0) {
            return emailErrors[0];
        }
        if (typeof emailErrors === 'string') {
            return emailErrors;
        }

        return 'Unable to submit merge request. Please try again.';
    }, []);

    const resetDialogState = useCallback(() => {
        setStep('details');
        setSecondaryEmail('');
        setConfirmCode('');
        setEmailTouched(false);
        setIsSubmitting(false);
        setServerError('');
    }, []);

    const _close = useCallback(() => {
        if (ui.bulkMode) {
            dispatch(
                updateUI({
                    bulkMode: false,
                    bulkEntries: []
                })
            );
        }
        resetDialogState();
        close && close();
    }, [close, ui.bulkMode, dispatch, resetDialogState]);

    const onContinue = useCallback(() => {
        const normalizedEmail = secondaryEmail.trim().toLowerCase();
        const myEmail = (user?.email || '').toLowerCase();

        setEmailTouched(true);
        if (normalizedEmail === myEmail) {
            setServerError('You cannot request a merge with the same account.');
            return;
        }
        if (!canContinue) {
            return;
        }
        setServerError('');
        setIsSubmitting(true);

        dispatch(
            UserActions.validateAccountMergeRequest({
                secondary_account_email: secondaryEmail.trim()
            })
        )
            .then((response) => {
                if (response?.payload?.status === 200) {
                    setStep('confirm');
                    return;
                }
                setEmailTouched(true);
                setServerError(extractSecondaryEmailValidation(response));
            })
            .catch((error) => {
                setEmailTouched(true);
                setServerError(extractSecondaryEmailValidation(error));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [
        canContinue,
        dispatch,
        extractSecondaryEmailValidation,
        secondaryEmail,
        user
    ]);

    const onConfirmMerge = useCallback(() => {
        setServerError('');
        setIsSubmitting(true);

        dispatch(
            UserActions.createAccountMergeRequest({
                secondary_account_email: secondaryEmail.trim()
            })
        )
            .then((response) => {
                if (response?.payload?.status === 201) {
                    setStep('success');
                    return;
                }

                setEmailTouched(true);
                setServerError(extractSecondaryEmailValidation(response));
            })
            .catch((error) => {
                setEmailTouched(true);
                setServerError(extractSecondaryEmailValidation(error));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [dispatch, extractSecondaryEmailValidation, secondaryEmail]);

    const onSuccessDone = useCallback(() => {
        dispatch(
            updateUI({
                toast: {
                    open: true,
                    message:
                        'Account merge request submitted. We will notify you once it is completed.',
                    severity: 'success'
                }
            })
        );
        if (onSuccess) {
            onSuccess();
        }
        _close();
    }, [_close, dispatch, onSuccess]);

    const onChangeConfirmCode = useCallback((e) => {
        setConfirmCode(e.target.value);
    }, []);

    const onChangeSecondaryEmail = useCallback(
        (e) => {
            setSecondaryEmail(e.target.value);
            if (serverError) {
                setServerError('');
            }
        },
        [serverError]
    );

    const renderDetailsStep = () => {
        return (
            <>
                <div className="merge-accounts-modal__content">
                    <div className="merge-accounts-modal__title">
                        Merge Accounts
                    </div>
                    <div className="merge-accounts-modal__subtitle">
                        Enter the secondary account email you want to merge into
                        this account. Your current account will remain the
                        primary account after approval.
                    </div>
                    <div
                        className="merge-accounts-form__group"
                        style={{ marginTop: '32px' }}>
                        <label
                            htmlFor="secondaryEmail"
                            className="merge-accounts-form__label">
                            Secondary account email
                        </label>
                        <input
                            type="email"
                            className="merge-accounts-modal_text merge-accounts-form__input"
                            onChange={onChangeSecondaryEmail}
                            onBlur={() => setEmailTouched(true)}
                            id="secondaryEmail"
                            name="secondary-email"
                            value={secondaryEmail}
                        />
                        {emailTouched && !isEmailValid && (
                            <div className="merge-accounts-modal__error">
                                Enter a valid email address.
                            </div>
                        )}
                        {serverError && (
                            <div className="merge-accounts-modal__error">
                                {serverError}
                            </div>
                        )}
                    </div>
                    <div className="merge-accounts-form__group">
                        <label
                            htmlFor="confirmCodeTitle"
                            className="merge-accounts-form__label">
                            Type {MERGE} to confirm
                        </label>
                        <input
                            type="text"
                            className="merge-accounts-modal_text merge-accounts-form__input"
                            onChange={onChangeConfirmCode}
                            id="confirmCodeTitle"
                            name="confirm-code"
                            value={confirmCode}
                        />
                    </div>
                </div>
                <div className="merge-accounts-modal__actions">
                    <button className="btn btn-outline" onClick={_close}>
                        Cancel
                    </button>
                    <button
                        className="btn btn-gradient"
                        onClick={onContinue}
                        disabled={!canContinue}>
                        {isSubmitting ? 'Submitting...' : 'Continue'}
                    </button>
                </div>
            </>
        );
    };

    const renderSuccessStep = () => {
        return (
            <>
                <div className="merge-accounts-modal__content">
                    <div className="merge-accounts-modal__title">
                        Merge Request Submitted
                    </div>
                    <div className="merge-accounts-modal__subtitle">
                        Your merge request for <strong>{secondaryEmail}</strong>{' '}
                        has been submitted for review. Once approved, this
                        account remains primary and the secondary account will
                        be merged and marked inactive.
                    </div>
                </div>
                <div className="merge-accounts-modal__actions">
                    <button
                        className="btn btn-gradient"
                        onClick={onSuccessDone}>
                        Done
                    </button>
                </div>
            </>
        );
    };

    const renderConfirmStep = () => {
        return (
            <>
                <div className="merge-accounts-modal__content">
                    <div className="merge-accounts-modal__title">
                        Confirm Account Merge
                    </div>
                    <div className="merge-accounts-modal__subtitle">
                        You are about to submit a merge request for this
                        secondary account. After admin approval, this account
                        stays primary and the secondary account is deactivated
                        and can no longer be used to log in.
                    </div>
                    <div className="merge-accounts-modal__preview">
                        <strong>Secondary account:</strong> {secondaryEmail}
                    </div>
                    {serverError && (
                        <div className="merge-accounts-modal__error">
                            {serverError}
                        </div>
                    )}
                </div>
                <div className="merge-accounts-modal__actions">
                    <button
                        className="btn btn-outline"
                        disabled={isSubmitting}
                        onClick={() => setStep('details')}>
                        Back
                    </button>
                    <button
                        className="btn btn-gradient"
                        disabled={isSubmitting}
                        onClick={onConfirmMerge}>
                        {isSubmitting ? 'Submitting...' : 'Confirm Merge'}
                    </button>
                </div>
            </>
        );
    };

    const renderStep = () => {
        switch (step) {
            case 'confirm': {
                return renderConfirmStep();
            }
            case 'success': {
                return renderSuccessStep();
            }
            case 'details':
            default: {
                return renderDetailsStep();
            }
        }
    };

    return (
        <Dialog
            fullWidth={true}
            open={open}
            onClose={_close}
            aria-labelledby="form-dialog-title">
            <div
                id="merge-accounts-modal"
                className="modal-box modal-box--merge-accounts modal-box--500">
                <div className="merge-accounts-modal">{renderStep()}</div>
            </div>
        </Dialog>
    );
};
const state = createStructuredSelector({
    ui: getUI,
    user: getUser
});
export default connect(state)(MergeAccountsDialog);
